import {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  type MouseEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme, alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import {
  ArrowLeft,
  Clock,
  Activity,
  Gauge,
  Map,
  ScanSearch,
  Download,
  Upload,
  History,
  AlertTriangle,
  RefreshCcw,
  type LucideIcon,
} from 'lucide-react';

import Card from '@/components/Card';
import { Button } from '@/components/Button';
import Fallback from '@/components/Fallback';
import { TableComponent, type Column } from '@/components/TableComponent';
import { useEquipmentContext } from '@/context/EquipmentContext';
import {
  DiagnosticsService,
  type DiagnosticHistoryEntry,
  type DiagnosticHistoryType,
  type DiagnosticType,
} from '@/services/diagnosticsService';
import { formatDateTime } from '@/utils/scanner';
import { parseResult, getResultState, getResultSummary } from './utils';

import { ToolToggleButton, ControlLabel, ControlGroup } from '../styles';
import {
  HistoryWrapper,
  HeaderRow,
  HeaderTitleStack,
  HistoryFilterGroup,
  StateWrapper,
  HistoryStateChip,
  HistoryTextTypography,
  HistoryResultTypography,
  TableRowAlignStack,
} from './styles';

type HistoryFilter =
  | 'all'
  | 'speed'
  | 'download'
  | 'upload'
  | 'ping'
  | 'tracert'
  | 'scanner';

/** Path segments requested per filter */
const FILTER_ENDPOINTS: Record<
  Exclude<HistoryFilter, 'all'>,
  DiagnosticType[]
> = {
  speed: ['download', 'upload'],
  download: ['download'],
  upload: ['upload'],
  ping: ['ping'],
  tracert: ['traceroute'],
  scanner: ['wifi'],
};

/** Filter toggle buttons, in display order */
const FILTER_OPTIONS: {
  value: HistoryFilter;
  labelKey: string;
  Icon?: LucideIcon;
}[] = [
  { value: 'all', labelKey: 'equipment.tools.history.filters.all' },
  { value: 'speed', labelKey: 'tools.speed.title', Icon: Gauge },
  {
    value: 'download',
    labelKey: 'equipment.tools.history.types.DOWNLOAD',
    Icon: Download,
  },
  {
    value: 'upload',
    labelKey: 'equipment.tools.history.types.UPLOAD',
    Icon: Upload,
  },
  { value: 'ping', labelKey: 'tools.ping.title', Icon: Activity },
  { value: 'tracert', labelKey: 'tools.traceroute.title', Icon: Map },
  { value: 'scanner', labelKey: 'scanner.title', Icon: ScanSearch },
];

interface DiagnosticsHistoryProps {
  /** Returns to the tools view. */
  onBack: () => void;
}

/** Lists every diagnostic run recorded for the equipment, filterable by tool. */
function DiagnosticsHistory({ onBack }: DiagnosticsHistoryProps) {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const { equipment } = useEquipmentContext();

  const [entries, setEntries] = useState<DiagnosticHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [filter, setFilter] = useState<HistoryFilter>('all');
  const [page, setPage] = useState(0);

  const equipmentId = equipment?.id;

  // Shared by the mount fetch and the retry button, so neither writes state
  // after the view is closed.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const requestRef = useRef(0);

  const loadHistory = useCallback(async () => {
    if (!equipmentId) return;

    const requestId = ++requestRef.current;
    const isCurrent = () =>
      mountedRef.current && requestRef.current === requestId;

    setLoading(true);
    setFailed(false);

    try {
      // 'all' hits the unscoped endpoint; every other filter asks the backend
      // for just the tools it covers.
      const endpoints: (DiagnosticType | undefined)[] =
        filter === 'all' ? [undefined] : FILTER_ENDPOINTS[filter];

      const responses = await Promise.all(
        endpoints.map((type) =>
          DiagnosticsService.getHistory(String(equipmentId), type)
        )
      );
      if (!isCurrent()) return;

      // Newest run first; speed filter interleaves two responses.
      const sorted = responses
        .flatMap((data) => (Array.isArray(data) ? data : []))
        .sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
      setEntries(sorted);
    } catch {
      if (!isCurrent()) return;
      setEntries([]);
      setFailed(true);
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [equipmentId, filter]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const typeStyles: Record<
    DiagnosticHistoryType,
    { color: string; Icon: typeof Activity }
  > = useMemo(
    () => ({
      PING: { color: theme.palette.primary.main, Icon: Activity },
      TRACEROUTE: { color: theme.palette.secondary['01'] || '', Icon: Map },
      DOWNLOAD: { color: theme.palette.status.online, Icon: Gauge },
      UPLOAD: { color: theme.palette.status.info, Icon: Gauge },
      WIFI: { color: theme.palette.status.warning, Icon: ScanSearch },
    }),
    [theme]
  );

  /** Green when the device reported success, red for every failure state. */
  const stateColor = useCallback(
    (state: DiagnosticsState) => {
      if (state === 'Complete') return theme.palette.status.online;
      if (state === 'None') return theme.palette.grey['01'] || '';
      return theme.palette.status.offline;
    },
    [theme]
  );

  const columns: Column<DiagnosticHistoryEntry>[] = useMemo(
    () => [
      // Date column
      {
        id: 'createdAt',
        label: t('equipment.tools.history.columns.date'),
        width: '210px',
        render: (_, row) => (
          <TableRowAlignStack>
            <Clock size={14} color={theme.palette.white['06']} />
            <HistoryTextTypography variant="body2">
              {formatDateTime(row.createdAt, i18n.language)}
            </HistoryTextTypography>
          </TableRowAlignStack>
        ),
      },
      // Tool column
      {
        id: 'type',
        label: t('equipment.tools.history.columns.tool'),
        width: '160px',
        render: (_, row) => {
          const { color, Icon } = typeStyles[row.type] ?? {
            color: theme.palette.grey['01'] || '',
            Icon: Activity,
          };
          return (
            <HistoryStateChip
              icon={<Icon size={13} color={color} />}
              label={t(`equipment.tools.history.types.${row.type}`, {
                defaultValue: row.type,
              })}
              size="small"
              sx={{ backgroundColor: alpha(color, 0.16), color }}
            />
          );
        },
      },
      // Status column
      {
        id: 'status',
        label: t('equipment.tools.history.columns.status'),
        width: '150px',
        render: (_, row) => {
          const state = getResultState(parseResult(row.result));
          const color = stateColor(state);
          return (
            <HistoryStateChip
              label={t(`equipment.tools.history.states.${state}`, {
                defaultValue: state,
              })}
              size="small"
              sx={{ backgroundColor: alpha(color, 0.16), color }}
            />
          );
        },
      },
      // Result column
      {
        id: 'result',
        label: t('equipment.tools.history.columns.result'),
        render: (_, row) => {
          const summary = getResultSummary(
            row.type,
            parseResult(row.result),
            t
          );
          return (
            <HistoryResultTypography variant="body2">
              {summary.length > 0 ? summary.join(' · ') : '—'}
            </HistoryResultTypography>
          );
        },
      },
    ],
    [t, theme, i18n.language, typeStyles, stateColor]
  );

  const header = (
    <HeaderRow>
      <Tooltip title={t('common.back')}>
        <IconButton aria-label={t('common.back')} onClick={onBack}>
          <ArrowLeft size={20} color={theme.palette.white['06']} />
        </IconButton>
      </Tooltip>
      <HeaderTitleStack>
        <Typography variant="h4">
          {t('equipment.tools.history.title')}
        </Typography>
        <Typography variant="caption" color={theme.palette.white['06']}>
          {t('equipment.tools.history.subtitle')}
        </Typography>
      </HeaderTitleStack>
    </HeaderRow>
  );

  const handleFilterChange = (
    _event: MouseEvent<HTMLElement>,
    value: HistoryFilter | null
  ) => {
    if (value === null) return;
    setFilter(value);
    setPage(0);
  };

  return (
    <HistoryWrapper>
      {header}

      <ControlGroup>
        <ControlLabel>{t('equipment.tools.history.filterLabel')}</ControlLabel>
        <HistoryFilterGroup
          value={filter}
          exclusive
          onChange={handleFilterChange}
        >
          {FILTER_OPTIONS.map(({ value, labelKey, Icon }) => (
            <ToolToggleButton key={value} value={value}>
              {Icon && <Icon size="16px" />}
              {t(labelKey)}
            </ToolToggleButton>
          ))}
        </HistoryFilterGroup>
      </ControlGroup>

      {loading && (
        <Card>
          <Fallback
            text={t('equipment.tools.history.loading')}
            variant="compact"
          />
        </Card>
      )}

      {/* Error state */}
      {!loading && failed && (
        <Card>
          <StateWrapper>
            <AlertTriangle size={40} color={theme.palette.status.offline} />
            <HistoryTextTypography variant="body1">
              {t('equipment.tools.history.error')}
            </HistoryTextTypography>
            <Button variant="outlined" height="36px" onClick={loadHistory}>
              <TableRowAlignStack>
                <RefreshCcw size={14} />
                {t('equipment.tools.history.retry')}
              </TableRowAlignStack>
            </Button>
          </StateWrapper>
        </Card>
      )}

      {!loading && !failed && (
        <Card padding="20px">
          {entries.length === 0 ? (
            <StateWrapper>
              <History size={40} color={theme.palette.white['06']} />
              <HistoryTextTypography variant="body1">
                {t('equipment.tools.history.empty')}
              </HistoryTextTypography>
            </StateWrapper>
          ) : (
            <TableComponent<DiagnosticHistoryEntry>
              data={entries}
              columns={columns}
              page={page}
              setPage={setPage}
              rowsPerPage={10}
              size="small"
            />
          )}
        </Card>
      )}
    </HistoryWrapper>
  );
}

export default DiagnosticsHistory;
